/**
 * Display formatting for the vitals and the ingredient grid.
 *
 * Every number the editor shows goes through here so that a gravity is always
 * four decimal places, a percentage always one, and a column of them always
 * lines up (`tabular-nums` does the rest).
 */

import { ebcToLovibond, lovibondToEbc } from '@beerwright/catalog';
import type { UnitPreferences } from '@beerwright/commands';
import { brixToSg, ebcToSrm, platoToSg, sgToBrix, sgToPlato, srmToEbc } from '@beerwright/core';

/** `1.0503` → `1.050`. Three decimals is what a hydrometer reads. */
export function formatGravity(sg: number, units?: UnitPreferences): string {
  if (!Number.isFinite(sg)) return '—';
  if (units?.gravity === 'plato') return `${sgToPlato(sg).toFixed(1)} °P`;
  if (units?.gravity === 'brix') return `${sgToBrix(sg).toFixed(1)} °Bx`;
  return sg.toFixed(3);
}

/**
 * The bare number a gravity input shows, in the preferred scale and with no
 * unit suffix — what the user is about to edit.
 *
 * Split from {@link formatGravity} because an input's value must round-trip
 * through {@link parseGravity} unchanged, and `12.0 °P` does not.
 */
export function gravityInputValue(sg: number, units?: UnitPreferences): string {
  if (!Number.isFinite(sg)) return '';
  if (units?.gravity === 'plato') return sgToPlato(sg).toFixed(2);
  if (units?.gravity === 'brix') return sgToBrix(sg).toFixed(2);
  return sg.toFixed(3);
}

/**
 * Read a typed gravity in the user's preferred scale back to canonical SG.
 *
 * The scale is the user's preference, with **one** override: a value in
 * `[0.9, 1.2]` is read as SG whatever the preference says. A brewer set to
 * °Plato who types `1.048` off a hydrometer means 1.048 SG — 1.048 °P is
 * water — and the two scales do not otherwise overlap anywhere near brewing
 * strengths, so the guess cannot misfire. Everything outside that window is
 * taken at its face value in the preferred scale.
 *
 * A trailing unit is stripped if present, so `12 °P` and `1.048 SG` both work
 * regardless of the setting.
 *
 * @returns The SG, or `null` when the text is not a number.
 */
export function parseGravity(text: string, units?: UnitPreferences): number | null {
  const trimmed = text.trim();
  const suffix = /\s*(°?p|plato)$/i.test(trimmed)
    ? 'plato'
    : /\s*(°?bx|brix)$/i.test(trimmed)
      ? 'brix'
      : /\s*sg$/i.test(trimmed)
        ? 'sg'
        : null;
  const bare = trimmed.replace(/\s*(°?p|°?bx|brix|plato|sg)$/i, '');
  if (bare === '') return null;
  const value = Number(bare);
  if (!Number.isFinite(value)) return null;

  const scale = suffix ?? (value >= 0.9 && value <= 1.2 ? 'sg' : (units?.gravity ?? 'sg'));
  if (scale === 'plato') return platoToSg(value);
  if (scale === 'brix') return brixToSg(value);
  return value;
}

/** Bitterness, whole IBU. */
export function formatIbu(ibu: number): string {
  return Number.isFinite(ibu) ? ibu.toFixed(0) : '—';
}

/** Colour in the preferred scale. */
export function formatColor(srm: number, units?: UnitPreferences): string {
  if (!Number.isFinite(srm)) return '—';
  return units?.color === 'ebc' ? srmToEbc(srm).toFixed(0) : srm.toFixed(1);
}

/**
 * The bare number a **beer colour** input shows, in the preferred scale.
 *
 * {@link gravityInputValue}'s counterpart for colour, and split from
 * {@link formatColor} for the same reason: an input's value has to round-trip
 * through {@link parseColor} unchanged. A tenth in SRM and a whole number in
 * EBC, which is the precision each scale is read to.
 */
export function colorInputValue(srm: number, units?: UnitPreferences): string {
  if (!Number.isFinite(srm)) return '';
  return units?.color === 'ebc' ? srmToEbc(srm).toFixed(0) : srm.toFixed(1);
}

/**
 * Read a typed beer colour in the user's preferred scale back to SRM.
 *
 * The scale is the preference, and a trailing `SRM` or `EBC` overrides it — the
 * same courtesy {@link parseGravity} extends to a hydrometer reading typed into
 * a Plato workspace. There is no numeric window to guess from here: 20 is a
 * plausible figure in either scale, so an unsuffixed number is taken at face
 * value in whichever one the workspace shows.
 *
 * @returns The SRM, or `null` when the text is not a number.
 */
export function parseColor(text: string, units?: UnitPreferences): number | null {
  const trimmed = text.trim();
  const suffix = /\s*ebc$/i.test(trimmed) ? 'ebc' : /\s*srm$/i.test(trimmed) ? 'srm' : null;
  const bare = trimmed.replace(/\s*(srm|ebc)$/i, '');
  if (bare === '') return null;
  const value = Number(bare);
  if (!Number.isFinite(value)) return null;
  const scale = suffix ?? (units?.color === 'ebc' ? 'ebc' : 'srm');
  return scale === 'ebc' ? ebcToSrm(value) : value;
}

/**
 * A **malt's** colour — published in degrees Lovibond — in the preferred scale.
 *
 * Separate from {@link formatColor} because the number on a malt spec sheet is
 * not a beer colour. °L and SRM are read as one-to-one for a grain (the whole
 * industry quotes "60 °L crystal" and "60 SRM crystal" interchangeably), so an
 * SRM reader sees the maltster's figure unchanged; an EBC reader sees it
 * through the maltster's own conversion — `lovibondToEbc`, the same one the
 * catalog's `colorEbc` is checked against — rather than through the Daniels
 * beer-colour fit, which would disagree with the EBC printed on the bag.
 *
 * Two decimal places' worth of judgement: one below 100, none above, because a
 * pale malt is told from another by tenths and a roast never is.
 */
export function formatMaltColor(lovibond: number, units?: UnitPreferences): string {
  if (!Number.isFinite(lovibond)) return '—';
  if (units?.color !== 'ebc') return lovibond.toFixed(1);
  const ebc = lovibondToEbc(lovibond);
  return ebc.toFixed(ebc >= 100 ? 0 : 1);
}

/**
 * A stored °L malt colour as the *number* an editable colour field shows.
 *
 * {@link formatMaltColor} for a readout; this is for the cells and inputs whose
 * value is edited and written back, which need the round trip rather than a
 * string. Rounded to the tenth an EBC field displays so that opening a row and
 * saving it unchanged cannot move the number.
 */
export function maltColorShown(lovibond: number, units?: UnitPreferences): number {
  if (units?.color !== 'ebc') return lovibond;
  return Math.round(lovibondToEbc(lovibond) * 10) / 10;
}

/** What a number typed into that field means in °L. @see maltColorShown */
export function maltColorStored(shown: number, units?: UnitPreferences): number {
  if (units?.color !== 'ebc') return shown;
  return Math.round(ebcToLovibond(shown) * 100) / 100;
}

/**
 * A style's colour band in the preferred scale, e.g. `4–7` SRM or `8–14` EBC.
 *
 * An em dash for the bands the guideline genuinely omits.
 */
export function formatColorBand(
  band: { readonly min: number; readonly max: number } | null,
  units?: UnitPreferences,
): string {
  if (band === null) return '—';
  const scale = (srm: number) => (units?.color === 'ebc' ? srmToEbc(srm) : srm);
  return `${scale(band.min).toFixed(0)}–${scale(band.max).toFixed(0)}`;
}

/** A percentage, one decimal, no unit. */
export function formatPercent(value: number, decimals = 1): string {
  return Number.isFinite(value) ? value.toFixed(decimals) : '—';
}

/** A plain number with a fixed number of decimals. */
export function formatNumber(value: number, decimals = 1): string {
  return Number.isFinite(value) ? value.toFixed(decimals) : '—';
}

/**
 * A count of tablets or packets: `1`, `0.5`, `0.25`. Half and quarter tablets
 * are normal doses, so this keeps up to two decimals and drops trailing zeros.
 */
export function formatCount(value: number): string {
  return Number.isFinite(value) ? String(Number(value.toFixed(2))) : '—';
}

/** `2026-08-15T…` → `15 Aug 2026`. */
export function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(timestamp));
}

/** `just now`, `4 min ago`, `yesterday`, then a date. */
export function formatRelative(timestamp: number, now = Date.now()): string {
  const seconds = Math.round((now - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return formatDate(timestamp);
}

/**
 * How long ago something was, at the scale a cupboard cares about.
 *
 * `formatRelative` gives up after a week and prints a date, which is right for
 * "when did this sync" and useless for "is this sack old". A lot's age is
 * measured in months and years, and the question behind it — is this still
 * worth brewing with — does not turn on a fortnight.
 *
 * Deliberately says nothing about whether the age is *bad*. There is no shelf
 * life we can source per product, so the row states the fact and leaves the
 * judgement to the brewer (PLAN-HOP-AGE D-HA5).
 */
export function formatAge(isoDate: string, now = Date.now()): string | null {
  const then = Date.parse(isoDate);
  if (Number.isNaN(then)) return null;
  const days = Math.floor((now - then) / 86_400_000);
  if (days < 0) return null;
  if (days < 30) return 'this month';
  const months = Math.round(days / 30.44);
  if (months < 24) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return `${years} years ago`;
}

/** The colour scale's own unit label, for a heading. */
export function colorUnitLabel(units?: UnitPreferences): string {
  return units?.color === 'ebc' ? 'EBC' : 'SRM';
}

/** The gravity scale's own unit label. */
export function gravityUnitLabel(units?: UnitPreferences): string {
  return units?.gravity === 'plato' ? '°P' : 'SG';
}

/**
 * Calories in a serving that matches the volume preference.
 *
 * US volume uses a 12 oz can; metric uses a 330 mL bottle. The engine already
 * computes both — this only picks which one to print.
 */
export function formatCalories(
  vitals: {
    readonly caloriesPer330ml: number;
    readonly caloriesPer12UsFlOz: number;
  },
  units?: UnitPreferences,
): string {
  if (units?.volume === 'us') {
    return `${formatNumber(vitals.caloriesPer12UsFlOz, 0)} /12 oz`;
  }
  return `${formatNumber(vitals.caloriesPer330ml, 0)} /330 mL`;
}
